import { useState, useEffect } from 'react'

import type { HcStructureMenuCustomEvent } from '@hotmart-org-ca/cosmos-web'
import { useDecision } from '@optimizely/react-sdk'
import { HttpCodes } from '@space-lib/constants'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useRequest from '@space-shell/common/hooks/useRequest'
import { UserEndpoints, UserService } from '@space-shell/common/services/user'
import { CREATE_PROFILE_STORAGE_KEY, useAuthStore } from '@space-shell/core/store'
import type { AxiosError } from 'axios'

/**
 * Hook for creating a profile on Sparkle.
 *
 * This hook leverages remote decision-making and authentication state to trigger
 * a user profile creation request. The request is enabled if:
 * - The user is authenticated.
 * - The profile has not been created yet (verified via sessionStorage).
 * - The feature flag 'com_adm_web_enable_sparkle_create_profile' is enabled.
 * - A valid authentication token is available.
 * - The request has been activated via a 'toggle' event.
 *
 * The request is executed using the `useRequest` hook. On success, or if a conflict
 * error (HTTP 409) occurs, the profile is marked as created in sessionStorage to
 * prevent redundant requests.
 *
 * NOTE: This client-side logic is temporary and should be removed as soon as possible.
 * Its functionality should be isolated and handled in the backend.
 *
 * @returns {Object} An object containing:
 *   - {@link isLoadingSparkleCreateProfile} {boolean} - Indicates whether the profile creation request is loading.
 */
export default function useSparkleCreateProfile(): { isLoadingSparkleCreateProfile: boolean } {
  const { authStatus } = useAuthContext()
  const { token, expires_at } = useAuthStore()
  const [shouldEnableRequest, setShouldEnableRequest] = useState(false)
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED

  const [{ enabled: isEnabledSparkleCreateProfile }] = useDecision(
    'com_adm_web_enable_sparkle_create_profile'
  )
  const hasAlreadyCalledCreateProfile =
    sessionStorage.getItem(CREATE_PROFILE_STORAGE_KEY) === 'true'

  const isRequestEnabled =
    isAuthenticated &&
    !hasAlreadyCalledCreateProfile &&
    isEnabledSparkleCreateProfile &&
    Boolean(token) &&
    shouldEnableRequest

  const sparkleCreateProfileRequest = useRequest(
    [UserEndpoints.create()],
    () => UserService.createProfile(token, expires_at),
    {
      enabled: isRequestEnabled,
      onSuccess: () => {
        sessionStorage.setItem(CREATE_PROFILE_STORAGE_KEY, 'true')
      },
      onError: (error) => {
        if ((error as unknown as AxiosError).response?.status === HttpCodes.CONFLICT) {
          sessionStorage.setItem(CREATE_PROFILE_STORAGE_KEY, 'true')
        }
      }
    }
  )

  useEffect(() => {
    if (hasAlreadyCalledCreateProfile) {
      return
    }

    /**
     * Listener for the 'toggle' event that activates the profile creation request.
     * The 'toggle' event is fired when the menu is opened.
     *
     * @param {Event} event - The event object, with a detail property containing 'opened'.
     */
    function listener(event: Event) {
      const { opened } = (event as HcStructureMenuCustomEvent<{ opened: boolean }>).detail

      if (opened) {
        setShouldEnableRequest(true)
      }
    }

    window.addEventListener('toggle', listener)

    return () => {
      window.removeEventListener('toggle', listener)
    }
  }, [hasAlreadyCalledCreateProfile])

  const isLoadingSparkleCreateProfile = isRequestEnabled
    ? sparkleCreateProfileRequest.isLoading
    : false

  return {
    isLoadingSparkleCreateProfile
  }
}
